import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  useReactTable,
} from '@tanstack/react-table';
import { SkeletonLoader, TableCell } from '@toggle/design-system';
import { isElementScrollbarVisible, useScrollListener } from '@toggle/helpers';
import clsx from 'clsx';
import React, {
  ReactNode,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { EntityCell } from '~/components/entity-cell/EntityCell';
import {
  getEmptyColumns,
  SCENARIO_ENTITY_COLUMN_ID,
} from '~/widgets/scenario/utils/scenario-table-utils/scenario-table-utils';

import { SortableTableHeaderCell } from '../sortable-table-header-cell/SortableTableHeaderCell';
import styles from './EmptyTable.module.scss';
import { useEmptyTableActions } from './hooks/useEmptyTableActions';

export interface EmptyTableProps {
  entitiesTags?: string[];
  children?: ReactNode;
}

const PAGE_SIZE = 20;

export const EmptyTable = ({ entitiesTags, children }: EmptyTableProps) => {
  const { t } = useTranslation('scenario');

  const data = useMemo(
    () => (entitiesTags ? entitiesTags.map(entityTag => ({ entityTag })) : []),
    [entitiesTags]
  );

  const [pagination, setPagination] = useState<PaginationState>(() => ({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  }));

  const containerRef = useRef<HTMLDivElement>(null);
  const columns = useMemo(() => getEmptyColumns(t), [t]);
  const table = useReactTable({
    data,
    columns,
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  useScrollListener(
    containerRef,
    () => {
      if (table.getCanNextPage()) {
        table.setPageSize(pagination.pageSize + PAGE_SIZE);
      }

      return false;
    },
    [table, pagination],
    { isGlobalScrollListened: false, offset: 45 }
  );

  useLayoutEffect(() => {
    const hasScroll = isElementScrollbarVisible(containerRef, 'vertical');

    if (hasScroll || !containerRef.current) {
      return;
    }

    if (table.getCanNextPage()) {
      table.setPageSize(pagination.pageSize + PAGE_SIZE);
    }
  }, [containerRef, pagination.pageSize]);

  const { getEnabledKeys, handleDropdownAction } = useEmptyTableActions();

  const { rows } = table.getRowModel();

  return (
    <div
      ref={containerRef}
      className={clsx(styles.emptyTableRoot, {
        [styles.notEmpty]: rows.length > 0,
      })}
    >
      <table className={styles.emptyTable} data-testid="scenario-table">
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <SortableTableHeaderCell
                  key={header.id}
                  header={header}
                  handleAction={handleDropdownAction(header.column)}
                  enabledKeys={getEnabledKeys(header.column)}
                  enableDropdown={
                    !!data.length && !!getEnabledKeys(header.column).length
                  }
                />
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.id}>
              {row
                .getVisibleCells()
                .filter(row => row.column.id === SCENARIO_ENTITY_COLUMN_ID)
                .map(cell => {
                  return (
                    <TableCell key={cell.id} isNumber={false}>
                      <EntityCell entityTag={row.original.entityTag} />
                    </TableCell>
                  );
                })}

              {index === 0 && (
                <td
                  className={styles.emptyCell}
                  data-testid="empty-cell"
                  colSpan={columns.length - 1}
                  rowSpan={rows.length}
                >
                  <div
                    className={
                      rows.length > PAGE_SIZE
                        ? styles.stickyContent
                        : styles.absoluteContent
                    }
                  >
                    {children}
                  </div>
                </td>
              )}
            </tr>
          ))}

          {table.getCanNextPage() && (
            <tr data-testid="loading-row">
              {rows[0].getVisibleCells().map(cell => (
                <TableCell key={cell.id} isNumber={false}>
                  <SkeletonLoader
                    areas={[
                      {
                        styles: {
                          width: '100%',
                          height: '20px',
                          borderRadius: '8px',
                        },
                      },
                    ]}
                  />
                </TableCell>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
